/**
 * Cross-import format adapters
 *
 * Pure functions for converting between import card types so MtgCraft backups
 * and text formats can be imported into any target (collection, deck, list).
 *
 * All adapters require a CardIndexLookup for name/ID resolution.
 */
import type { DeckImportCard } from '@/features/decks/lib/deckImportExport';
import type { ParsedListCard } from '@/features/lists/utils/parsers';
import type { MinimalCard } from '@/services/cardDatabase';
import type { CollectionCard, Condition, Finish, Language } from '@/types/card';
import type { ImportCard } from '@/types/schemas';

import type { CollectionCardBackup, CollectionFolderBackup, DeckCardBackup, ListCardBackup } from '../types';

// ============================================================================
// Printing Preference
// ============================================================================

export type PrintingPreference = 'exact' | 'collection';

// ============================================================================
// Card Index Lookup Interface
// ============================================================================

/**
 * Minimal card index lookups needed by adapters.
 * Consumers pass functions from useCardIndex or atom maps.
 */
export interface CardIndexLookup {
  getCardById: (id: string) => MinimalCard | null;
  getCardByName: (name: string) => MinimalCard | null;
  getCardByOracleId: (oracleId: string) => MinimalCard | null;
}

// ============================================================================
// Deck → Collection
// ============================================================================

/**
 * Convert deck import cards to collection import cards.
 * Zone info is discarded; finish is preserved.
 */
export function deckCardsToImportCards(cards: DeckImportCard[], index: CardIndexLookup): ImportCard[] {
  return cards.map((card) => {
    // Resolve Scryfall ID if not already present
    let scryfallId: string | null = card.scryfallId ?? null;
    if (!scryfallId && card.name) {
      const match = index.getCardByName(card.name);
      if (match) scryfallId = match.id;
    }

    return {
      name: card.name,
      setCode: card.setCode,
      collectorNumber: card.collectorNumber,
      quantity: card.quantity,
      finish: card.finish ?? 'nonfoil',
      condition: 'NM',
      language: 'en',
      purchasePrice: null,
      purchaseCurrency: null,
      purchaseDate: null,
      isAltered: false,
      isPlaytest: false,
      folderName: null,
      sourceId: null,
      scryfallId,
    };
  });
}

// ============================================================================
// Deck → List
// ============================================================================

/**
 * Convert deck import cards to list cards.
 * Zone and printing info are discarded; resolved by oracle_id.
 */
export function deckCardsToListCards(cards: DeckImportCard[], index: CardIndexLookup): ParsedListCard[] {
  return cards.map((card) => {
    let oracleId: string | undefined;
    let scryfallId: string | undefined;
    // Normalize split card names: Moxfield uses " / " but Scryfall uses " // "
    const normalizedName = card.name.replace(/\s+\/\s+/g, ' // ');

    // Resolve oracle_id and scryfall_id from Scryfall ID or name
    if (card.scryfallId) {
      const match = index.getCardById(card.scryfallId);
      if (match) {
        oracleId = match.oracle_id ?? undefined;
        scryfallId = match.id;
      }
    }
    if (!oracleId && normalizedName) {
      const match = index.getCardByName(normalizedName);
      if (match) {
        oracleId = match.oracle_id ?? undefined;
        if (!scryfallId) scryfallId = match.id;
      }
    }

    return {
      name: normalizedName,
      oracle_id: oracleId,
      scryfall_id: scryfallId,
      quantity: card.quantity,
    };
  });
}

// ============================================================================
// ImportCard → List
// ============================================================================

/**
 * Convert collection import cards to list cards.
 * Printing and condition info are discarded.
 */
export function importCardsToListCards(cards: ImportCard[], index: CardIndexLookup): ParsedListCard[] {
  return cards.map((card) => {
    let oracleId: string | undefined;
    let scryfallId: string | undefined;

    if (card.scryfallId) {
      const match = index.getCardById(card.scryfallId);
      if (match) {
        oracleId = match.oracle_id ?? undefined;
        scryfallId = match.id;
      }
    }
    if (!oracleId) {
      const match = index.getCardByName(card.name);
      if (match) {
        oracleId = match.oracle_id ?? undefined;
        if (!scryfallId) scryfallId = match.id;
      }
    }

    return {
      name: card.name,
      oracle_id: oracleId,
      scryfall_id: scryfallId,
      quantity: card.quantity,
    };
  });
}

// ============================================================================
// Deck Backup → Collection
// ============================================================================

/**
 * Convert MtgCraft deck backup cards to collection import cards.
 * Resolves card data from index by scryfall_id.
 */
export function deckBackupToImportCards(cards: DeckCardBackup[], index: CardIndexLookup): ImportCard[] {
  return cards.map((card) => {
    const match = index.getCardById(card.scryfall_id);

    return {
      name: match?.name ?? '',
      setCode: match?.set ?? null,
      collectorNumber: match?.collector_number ?? null,
      quantity: card.quantity,
      finish: 'nonfoil',
      condition: 'NM',
      language: 'en',
      purchasePrice: null,
      purchaseCurrency: null,
      purchaseDate: null,
      isAltered: false,
      isPlaytest: false,
      folderName: null,
      sourceId: null,
      scryfallId: card.scryfall_id,
    };
  });
}

// ============================================================================
// Deck Backup → List
// ============================================================================

/**
 * Convert MtgCraft deck backup cards to list cards.
 * Zone and printing info are discarded; resolved by oracle_id.
 */
export function deckBackupToListCards(cards: DeckCardBackup[], index: CardIndexLookup): ParsedListCard[] {
  return cards.map((card) => {
    const match = index.getCardById(card.scryfall_id);

    return {
      name: match?.name ?? '',
      oracle_id: match?.oracle_id ?? undefined,
      scryfall_id: card.scryfall_id,
      quantity: card.quantity,
    };
  });
}

// ============================================================================
// List Backup → Deck
// ============================================================================

/**
 * Convert MtgCraft list backup cards to deck import cards.
 * All cards go to 'main' zone. Resolves scryfall_id from oracle_id.
 */
export function listBackupToDeckCards(cards: ListCardBackup[], index: CardIndexLookup): DeckImportCard[] {
  return cards.map((card) => {
    let scryfallId: string | undefined = card.scryfall_id;
    let setCode: string | null = null;
    let collectorNumber: string | null = null;

    // Resolve set/number from scryfall_id if present
    if (scryfallId) {
      const match = index.getCardById(scryfallId);
      if (match) {
        setCode = match.set;
        collectorNumber = match.collector_number;
      }
    }

    // Fallback: resolve from oracle_id
    if (!scryfallId && card.oracle_id) {
      const match = index.getCardByOracleId(card.oracle_id);
      if (match) {
        scryfallId = match.id;
        setCode = match.set;
        collectorNumber = match.collector_number;
      }
    }

    // Fallback to name
    if (!scryfallId) {
      const match = index.getCardByName(card.name);
      if (match) {
        scryfallId = match.id;
        setCode = match.set;
        collectorNumber = match.collector_number;
      }
    }

    return {
      name: card.name,
      setCode,
      collectorNumber,
      quantity: card.quantity,
      zone: 'main' as const,
      scryfallId,
    };
  });
}

// ============================================================================
// List Backup → Collection
// ============================================================================

/**
 * Convert MtgCraft list backup cards to collection import cards.
 * Resolves printing info from oracle_id via card index.
 */
export function listBackupToImportCards(cards: ListCardBackup[], index: CardIndexLookup): ImportCard[] {
  return cards.map((card) => {
    let match: MinimalCard | null = null;

    // Prefer scryfall_id from backup, then oracle_id, then name
    if (card.scryfall_id) {
      match = index.getCardById(card.scryfall_id);
    }
    if (!match && card.oracle_id) {
      match = index.getCardByOracleId(card.oracle_id);
    }
    if (!match) {
      match = index.getCardByName(card.name);
    }

    return {
      name: card.name,
      setCode: match?.set ?? null,
      collectorNumber: match?.collector_number ?? null,
      quantity: card.quantity,
      finish: 'nonfoil',
      condition: 'NM',
      language: 'en',
      purchasePrice: null,
      purchaseCurrency: null,
      purchaseDate: null,
      isAltered: false,
      isPlaytest: false,
      folderName: null,
      sourceId: null,
      scryfallId: card.scryfall_id ?? match?.id ?? null,
    };
  });
}

// ============================================================================
// Collection Backup → ImportCard (native restore)
// ============================================================================

const VALID_FINISHES = new Set<string>(['nonfoil', 'foil', 'etched']);
const VALID_CONDITIONS = new Set<string>(['M', 'NM', 'LP', 'MP', 'HP', 'D']);
const VALID_LANGUAGES = new Set<string>(['en', 'es', 'fr', 'de', 'it', 'pt', 'ja', 'ko', 'ru', 'zhs', 'zht', 'ph']);

/**
 * Convert MtgCraft collection backup cards to collection import cards.
 * Preserves all original fields (finish, condition, language, purchase info, flags).
 * Resolves folder names from the backup's folder list.
 */
export function collectionBackupToImportCards(
  cards: CollectionCardBackup[],
  folders: CollectionFolderBackup[],
  index: CardIndexLookup,
): ImportCard[] {
  // Build folder ID → name lookup
  const folderNameMap = new Map<string, string>();
  for (const folder of folders) {
    folderNameMap.set(folder.id, folder.name);
  }

  return cards.map((card) => {
    const match = index.getCardById(card.scryfall_id);

    return {
      name: match?.name ?? '',
      setCode: match?.set ?? null,
      collectorNumber: match?.collector_number ?? null,
      quantity: card.quantity,
      finish: (VALID_FINISHES.has(card.finish) ? card.finish : 'nonfoil') as Finish,
      condition: (VALID_CONDITIONS.has(card.condition) ? card.condition : 'NM') as Condition,
      language: (VALID_LANGUAGES.has(card.language) ? card.language : 'en') as Language,
      purchasePrice: card.purchase_price,
      purchaseCurrency: card.purchase_currency,
      purchaseDate: card.purchase_date,
      isAltered: card.is_altered ?? false,
      isPlaytest: card.is_playtest ?? false,
      folderName: card.folder_id ? (folderNameMap.get(card.folder_id) ?? null) : null,
      sourceId: card.source_id,
      scryfallId: card.scryfall_id,
    };
  });
}

// ============================================================================
// Edition Preference — Remap Printings to Collection
// ============================================================================

interface OwnedPrintingInfo {
  scryfallId: string;
  set: string;
  collectorNumber: string;
}

/**
 * Build a map of oracle_id → owned printing from the user's collection.
 * Takes the first occurrence per oracle_id (deterministic).
 */
function buildOwnedPrintingsMap(collectionCards: CollectionCard[], index: CardIndexLookup): Map<string, OwnedPrintingInfo> {
  const map = new Map<string, OwnedPrintingInfo>();

  for (const cc of collectionCards) {
    const card = index.getCardById(cc.scryfall_id);
    if (!card) continue;

    // First occurrence per oracle_id wins
    if (!card.oracle_id) continue;
    if (!map.has(card.oracle_id)) {
      map.set(card.oracle_id, {
        scryfallId: card.id,
        set: card.set,
        collectorNumber: card.collector_number,
      });
    }
  }

  return map;
}

/**
 * Remap import card scryfall_ids to owned printings from the user's collection.
 *
 * For each card, looks up its oracle_id via the card index, then checks if the
 * user owns any printing of that card. If so, substitutes the scryfall_id (and
 * set/collector info) with the owned printing. Falls back to original data if
 * not owned.
 */
export function remapImportCardsToCollection(
  cards: MatchedImportCardLike[],
  collectionCards: CollectionCard[],
  index: CardIndexLookup,
): MatchedImportCardLike[] {
  const ownedMap = buildOwnedPrintingsMap(collectionCards, index);

  return cards.map((card) => {
    // Resolve oracle_id from matched card or by name
    let oracleId: string | undefined;
    if (card.matched) {
      oracleId = card.matched.oracle_id ?? undefined;
    } else if (card.scryfallId) {
      const indexCard = index.getCardById(card.scryfallId);
      if (indexCard) oracleId = indexCard.oracle_id ?? undefined;
    }
    if (!oracleId) {
      const nameMatch = index.getCardByName(card.name);
      if (nameMatch) oracleId = nameMatch.oracle_id ?? undefined;
    }

    if (!oracleId) return card;

    const owned = ownedMap.get(oracleId);
    if (!owned) return card;

    // Substitute to owned printing
    const ownedMinimal = index.getCardById(owned.scryfallId);
    return {
      ...card,
      scryfallId: owned.scryfallId,
      setCode: owned.set,
      collectorNumber: owned.collectorNumber,
      matched: ownedMinimal ?? card.matched,
    };
  });
}

/**
 * Remap deck import card scryfall_ids to owned printings from the user's collection.
 */
export function remapDeckCardsToCollection(
  cards: DeckImportCard[],
  collectionCards: CollectionCard[],
  index: CardIndexLookup,
): DeckImportCard[] {
  const ownedMap = buildOwnedPrintingsMap(collectionCards, index);

  return cards.map((card) => {
    // Resolve oracle_id
    let oracleId: string | undefined;
    if (card.scryfallId) {
      const indexCard = index.getCardById(card.scryfallId);
      if (indexCard) oracleId = indexCard.oracle_id ?? undefined;
    }
    if (!oracleId) {
      const nameMatch = index.getCardByName(card.name);
      if (nameMatch) oracleId = nameMatch.oracle_id ?? undefined;
    }

    if (!oracleId) return card;

    const owned = ownedMap.get(oracleId);
    if (!owned) return card;

    return {
      ...card,
      scryfallId: owned.scryfallId,
      setCode: owned.set,
      collectorNumber: owned.collectorNumber,
    };
  });
}

/**
 * Minimal interface for import cards that have gone through matching.
 * Compatible with MatchedImportCardWithDuplicate.
 */
export interface MatchedImportCardLike extends ImportCard {
  matched?: MinimalCard | null;
  matchType?: 'exact' | 'name' | 'fuzzy' | 'none';
}
